/**
 * FamiliarisationSectionContent - Individual section content for familiarisation
 */

import React from 'react';
import { CheckCircle } from 'lucide-react';
import ClassificationBadge from './components/ClassificationBadge';
import RoleBadge from './components/RoleBadge';

const FamiliarisationSectionContent = ({
  section,
  checklistData,
  completedItems,
  isTopicCompleted,
  notes,
  onItemStatusChange,
  onTopicComplete,
  onNotesChange,
  onNAClick,
  viewMode
}) => {
  // If section is undefined, return null
  if (!section) {
    return null;
  }
  
  // Handle different data structures - items could be direct array or nested in checklist
  let items = [];
  if (checklistData?.items && Array.isArray(checklistData.items)) {
    items = checklistData.items;
  } else if (checklistData?.checklist && Array.isArray(checklistData.checklist)) {
    items = checklistData.checklist;
  } else if (section?.items && Array.isArray(section.items)) {
    items = section.items;
  } else if (section?.checklist && Array.isArray(section.checklist)) {
    items = section.checklist;
  }

  // Helper function to format N/A reasons for display
  const formatNAReason = (reason) => {
    // Handle custom reasons (format: "other: custom text")
    if (reason.startsWith('other: ')) {
      return reason.substring(7); // Remove "other: " prefix
    }
    
    // Map predefined reasons to human-readable text
    const reasonMap = {
      'not_required_rank': 'Not required by rank/role',
      'equipment_not_onboard': 'Equipment not onboard',
      'not_applicable_vessel': 'Not applicable to vessel type',
      'system_not_installed': 'System not installed',
      'electric_propulsion': 'Electric propulsion vessel',
      'no_passenger_ops': 'No passenger operations',
      'no_cargo_ops': 'No cargo/lifting operations',
      'open_deck_only': 'Open deck vessel only',
      'not_required_category': 'Not required for vessel category'
    };
    
    return reasonMap[reason] || reason;
  };

  // Calculate filtered items count (if provided)
  const filteredItemsCount = section?.filteredItemsCount || 0;
  const showFilteredNotice = filteredItemsCount > 0;

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div className="bg-cyan-50 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">{section?.title || 'Section'}</h3>
        <p className="text-gray-600 mb-4">{section?.description || ''}</p>
        {section?.reference && (
          <p className="text-sm text-cyan-600">Reference: {section.reference}</p>
        )}
        
        {/* Vessel Configuration Notice */}
        {showFilteredNotice && (
          <div className="mt-4 bg-cyan-100 border border-cyan-200 rounded-md p-3">
            <p className="text-sm text-cyan-800">
              <span className="font-medium">Vessel-specific:</span> {filteredItemsCount} item{filteredItemsCount > 1 ? 's' : ''} hidden based on vessel configuration
            </p>
          </div>
        )}
      </div>

      {/* Checklist Items */}
      {items.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg">
          <div className="px-4 py-3 border-b border-gray-200">
            <h4 className="text-base font-medium text-gray-900">Checklist Items</h4>
          </div>
          <div className="p-4 space-y-3">
            {items.map((item, index) => {
              // Use item.id if available, otherwise create one from section and index
              const itemId = item.id || `${section.section}-item-${index}`;
              const itemStatus = completedItems[itemId];
              const isCompleted = itemStatus?.status === 'completed';
              const isNA = itemStatus?.status === 'na';
              
              return (
                <div key={itemId} className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg">
                  <div className="flex-shrink-0 mt-0.5">
                    {!viewMode ? (
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            if (isCompleted) {
                              // Allow unchecking - clear the status
                              onItemStatusChange(section.section, itemId, null);
                            } else {
                              // Check the item
                              onItemStatusChange(section.section, itemId, 'completed');
                            }
                          }}
                          className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-colors ${
                            isCompleted
                              ? 'bg-green-500 border-green-500 text-white hover:bg-green-600'
                              : 'border-gray-300 hover:border-green-400 hover:bg-green-50'
                          }`}
                          title={isCompleted ? 'Click to uncheck' : 'Click to check'}
                        >
                          {isCompleted && <CheckCircle size={16} />}
                        </button>
                        <button
                          onClick={() => {
                            if (isNA) {
                              // Allow un-N/A - clear the status
                              onItemStatusChange(section.section, itemId, null);
                            } else {
                              // Show N/A reason dialog with item containing the itemId
                              onNAClick(section.section, { ...item, id: itemId });
                            }
                          }}
                          className={`px-3 py-1 text-xs rounded transition-colors ${
                            isNA
                              ? 'bg-amber-500 text-white hover:bg-amber-600'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                          }`}
                          title={isNA ? 'Click to clear N/A status' : 'Mark as not applicable'}
                        >
                          N/A
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className={`w-6 h-6 rounded border-2 flex items-center justify-center ${
                          isCompleted ? 'bg-green-500 border-green-500 text-white' :
                          isNA ? 'bg-amber-500 border-amber-500 text-white' :
                          'border-gray-300'
                        }`}>
                          {isCompleted && <CheckCircle size={16} />}
                          {isNA && <span className="text-xs font-bold">N/A</span>}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{item.text || item.item || item.topic}</p>
                    {item.description && (
                      <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {item.classification && (
                        <ClassificationBadge classification={item.classification} />
                      )}
                      {item.role && (
                        <RoleBadge role={item.role} />
                      )}
                      {item.priority === 'critical' && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">
                          Critical
                        </span>
                      )}
                      {isNA && itemStatus?.reason && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-amber-100 text-amber-800 rounded-full">
                          N/A: {formatNAReason(itemStatus.reason)}
                        </span>
                      )}
                      {item.isCustom && (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded-full">
                          Company Requirement
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Notes Section */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-4 py-3 border-b border-gray-200">
          <h4 className="text-base font-medium text-gray-900">Notes</h4>
        </div>
        <div className="p-4">
          <textarea
            value={notes}
            onChange={(e) => onNotesChange(section.section, e.target.value)}
            placeholder="Add any additional notes or observations..."
            className="w-full h-24 p-3 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500"
            disabled={viewMode}
          />
        </div>
      </div>

      {/* Section Sign-off */}
      {!viewMode && (
        <div className="bg-gray-50 rounded-lg p-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={isTopicCompleted}
              onChange={(e) => onTopicComplete(section.section, e.target.checked)}
              className="rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
            />
            <span className="ml-3 text-sm font-medium text-gray-900">
              I confirm this section has been completed and understood
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

export default FamiliarisationSectionContent;